import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Download, Loader2, Share2, Spade } from "lucide-react";
import { API, apiFetch } from "@/lib/gp";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const FIELD = "w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-white";

async function errorText(res) {
  const body = await res.json().catch(() => ({}));
  return typeof body.detail === "string" ? body.detail : "Something went wrong. Try again.";
}

export function ShareBuild() {
  usePageMeta({ path: "/builds/new", title: "Share a Balatro Run Build | GamePlat", description: "Share your Balatro run build and get Yorick's verdict.", indexable: false });
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [options, setOptions] = useState(null);
  const [form, setForm] = useState({ deck: "Red", stake: "White", main_hand: "Flush", jokers: "", final_ante: 8, best_hand_score: "", won: true, title: "", note: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { state: { from: "/builds/new" } });
  }, [loading, user, navigate]);
  useEffect(() => {
    fetch(`${API}/builds/options`).then((r) => r.json()).then(setOptions).catch(() => toast.error("Could not load build options."));
  }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await apiFetch("/builds", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          jokers: form.jokers.split(",").map((j) => j.trim()).filter(Boolean),
          final_ante: Number(form.final_ante),
          best_hand_score: form.best_hand_score === "" ? null : Number(form.best_hand_score),
        }),
      });
      if (!res.ok) throw new Error(await errorText(res));
      const { build } = await res.json();
      navigate(`/build/${build.id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!options) return <div className="py-24 text-center text-white/60"><Loader2 className="mx-auto animate-spin" /></div>;
  const select = (key, list) => (
    <select value={form[key]} onChange={set(key)} className={FIELD}>
      {list.map((o) => <option key={o} value={o} className="bg-gp-bg">{o}</option>)}
    </select>
  );

  return (
    <main className="mx-auto max-w-2xl px-5 py-12">
      <h1 className="font-unbounded font-black text-3xl">Share your Balatro run</h1>
      <p className="mt-2 text-white/60">Post your build, get Yorick's verdict, and share the link with your friends or community.</p>
      <form onSubmit={submit} className="glass rounded-2xl p-6 mt-6 grid gap-4" data-testid="share-build-form">
        <div className="grid sm:grid-cols-3 gap-4">
          <label className="text-sm text-white/70">Deck{select("deck", options.decks)}</label>
          <label className="text-sm text-white/70">Stake{select("stake", options.stakes)}</label>
          <label className="text-sm text-white/70">Main hand{select("main_hand", options.hands)}</label>
        </div>
        <label className="text-sm text-white/70">Jokers, left to right (comma separated, up to {options.max_jokers})
          <Input value={form.jokers} onChange={set("jokers")} placeholder="Blueprint, Green Joker, Baron" required />
        </label>
        <div className="grid sm:grid-cols-3 gap-4 items-end">
          <label className="text-sm text-white/70">Final ante<Input type="number" min="1" max="100" value={form.final_ante} onChange={set("final_ante")} required /></label>
          <label className="text-sm text-white/70">Best hand score<Input type="number" min="0" value={form.best_hand_score} onChange={set("best_hand_score")} placeholder="Optional" /></label>
          <label className="flex items-center gap-2 text-sm text-white/70 pb-2"><input type="checkbox" checked={form.won} onChange={set("won")} /> I won this run</label>
        </div>
        <label className="text-sm text-white/70">Title<Input value={form.title} onChange={set("title")} maxLength={60} placeholder="Optional" /></label>
        <label className="text-sm text-white/70">Note<Textarea value={form.note} onChange={set("note")} maxLength={280} placeholder="Optional — no links" /></label>
        <Button disabled={busy} className="rounded-full bg-gp-blue font-bold">
          {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Spade size={16} className="mr-2" />} Get Yorick's verdict & share
        </Button>
      </form>
    </main>
  );
}

function drawCard(build) {
  const c = document.createElement("canvas");
  c.width = 1200;
  c.height = 630;
  const g = c.getContext("2d");
  g.fillStyle = "#070b12";
  g.fillRect(0, 0, c.width, c.height);
  g.fillStyle = "#1677ff";
  g.fillRect(0, 0, c.width, 12);
  const line = (text, y, size, color = "#ffffff") => {
    g.fillStyle = color;
    g.font = `bold ${size}px sans-serif`;
    g.fillText(text, 60, y, 1080);
  };
  line(build.title || `${build.deck} Deck · ${build.main_hand}`, 110, 56);
  line(`${build.player_name} · ${build.stake} Stake · ${build.won ? "Won" : `Ante ${build.final_ante}`}`, 175, 32, "#f0c040");
  line(`Jokers: ${build.jokers.join(", ")}`, 260, 30);
  const words = `Yorick: ${build.verdict}`.split(" ");
  let row = "";
  let y = 350;
  words.forEach((w) => {
    if (g.measureText(`${row}${w} `).width > 1080 && row) { line(row, y, 30, "#9fc5ff"); row = ""; y += 44; }
    row += `${w} `;
  });
  line(row, y, 30, "#9fc5ff");
  line("gameplatplay.com", 580, 28, "#ffffffaa");
  return c;
}

export function BuildCard() {
  const { id } = useParams();
  const [build, setBuild] = useState(null);
  const [missing, setMissing] = useState(false);
  usePageMeta({ path: `/build/${id}`, title: "Balatro Run Build | GamePlat", description: "A Balatro run build with Yorick's verdict.", indexable: false });

  useEffect(() => {
    fetch(`${API}/builds/${encodeURIComponent(id)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setBuild(d.build))
      .catch(() => setMissing(true));
  }, [id]);

  const url = window.location.href;
  const copy = () => navigator.clipboard.writeText(url).then(() => toast.success("Link copied."), () => toast.error("Copy failed."));
  const share = () => (navigator.share ? navigator.share({ title: "My Balatro run", url }).catch(() => {}) : copy());
  const download = () => {
    const a = document.createElement("a");
    a.href = drawCard(build).toDataURL("image/png");
    a.download = `balatro-build-${build.id}.png`;
    a.click();
  };

  if (missing) return <div className="py-24 text-center text-white/60">That build doesn't exist or was deleted. <Link to="/builds/new" className="text-gp-blue underline">Share your own</Link>.</div>;
  if (!build) return <div className="py-24 text-center text-white/60"><Loader2 className="mx-auto animate-spin" /></div>;

  return (
    <main className="mx-auto max-w-2xl px-5 py-12" data-testid="build-card">
      <div className="glass rounded-3xl p-7 ring-1 ring-gp-gold/30">
        <p className="font-vt323 uppercase tracking-widest text-gp-gold text-sm">{build.player_name} · {build.stake} Stake</p>
        <h1 className="font-unbounded font-black text-3xl mt-2">{build.title || `${build.deck} Deck · ${build.main_hand}`}</h1>
        <p className="mt-3 text-white/70">{build.won ? "Won the run" : `Reached Ante ${build.final_ante}`}{build.best_hand_score != null && ` · Best hand ${build.best_hand_score.toLocaleString()}`}</p>
        <ol className="mt-5 flex flex-wrap gap-2">
          {build.jokers.map((j, i) => <li key={i} className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-sm">{i + 1}. {j}</li>)}
        </ol>
        {build.note && <p className="mt-5 text-white/75">"{build.note}"</p>}
        <p className="mt-6 rounded-2xl bg-gp-blue/10 border border-gp-blue/30 p-4 text-white/90"><strong>Yorick:</strong> {build.verdict}</p>
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <Button onClick={share} className="rounded-full bg-gp-blue font-bold"><Share2 size={16} className="mr-2" /> Share</Button>
        <Button onClick={copy} variant="outline" className="rounded-full border-white/15 bg-white/5"><Copy size={16} className="mr-2" /> Copy link</Button>
        <Button onClick={download} variant="outline" className="rounded-full border-white/15 bg-white/5"><Download size={16} className="mr-2" /> Download image</Button>
        <Link to="/builds/new" className="self-center text-sm text-white/60 hover:text-white">Share your own run →</Link>
      </div>
    </main>
  );
}
